import { createDateTimeFormatter } from "./locale";
import { parseISO } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz'

const IS_MILLIS_CUTOFF = 1000000000000;
const MS_PER_DAY = 86400 * 1000;
const NOW_THRESHOLD = 45 * 1000;
const RELATIVE_THRESHOLD = 60 * 60 * 1000;

const DATETIME_FORMATTER = new Map<string, Intl.DateTimeFormat>();
const DATETIME_FORMAT: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

const DATE_FORMATTER = new Map<string, Intl.DateTimeFormat>();
const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
};

const TIME_FORMATTER = new Map<string, Intl.DateTimeFormat>();
const TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
};

const WEEKDAY_FORMATTER = new Map<string, Intl.DateTimeFormat>();
const WEEKDAY_FORMAT: Intl.DateTimeFormatOptions = {
  weekday: "long",
};

function getOrCreateFormatter(
  map: Map<string, Intl.DateTimeFormat>,
  locale: string,
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat {
  const existing = map.get(locale);
  if (existing) {
    return existing;
  } else {
    const created = createDateTimeFormatter(locale, options);
    map.set(locale, created);
    return created;
  }
}

function localizeDateTime(date: Date, locale: string) {
  return getOrCreateFormatter(
    DATETIME_FORMATTER,
    locale,
    DATETIME_FORMAT
  ).format(date);
}

function localizeDate(date: Date, locale: string) {
  return getOrCreateFormatter(DATE_FORMATTER, locale, DATE_FORMAT).format(date);
}

function localizeTime(date: Date, locale: string) {
  if (locale === "de") {
    // Hacky way to add Uhr in german language only, no i18n because other language don't have this (for now)
    return (
      getOrCreateFormatter(TIME_FORMATTER, locale, TIME_FORMAT).format(date) +
      " Uhr"
    );
  } else {
    return getOrCreateFormatter(TIME_FORMATTER, locale, TIME_FORMAT).format(
      date
    );
  }
}

function localizeWeekday(date: Date, locale: string) {
  return getOrCreateFormatter(WEEKDAY_FORMATTER, locale, WEEKDAY_FORMAT).format(
    date
  );
}

function beginningOfDay(ts?: number) {
  const date = ts ? new Date(ts) : new Date();
  date.setHours(0, 0, 0, 0);
  return date;
}


export function getFormattedCreationDate(input: string): string {
  const timeZone = 'Europe/Berlin'; // Corresponding to de-DE  
  const inputDateUtc = fromZonedTime(parseISO(input), timeZone);
  const inputDate = toZonedTime(inputDateUtc, timeZone);
  const now = toZonedTime(new Date(), timeZone);

  const diffInMs = now.getTime() - inputDate.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));

  if (diffInMinutes == 1) {
      return `vor ${diffInMinutes} Minute`;
  } else if (diffInMinutes < 60) {
    return `vor ${diffInMinutes} Minuten`;
  } else if (diffInHours == 1) {
    return `vor ${diffInHours} Stunde`;
  } else if (diffInHours < 24) {
    return `vor ${diffInHours} Stunden`;
  } else {
    return getFormattedCreationDateFixed(input);
  }
}

export function getFormattedCreationDateFixed(input: string): string {
  const timeZone = 'Europe/Berlin'; // Corresponding to de-DE  
  const inputDateUtc = fromZonedTime(parseISO(input), timeZone);
  const inputDate = toZonedTime(inputDateUtc, timeZone);

  const day = inputDate.getDate().toString().padStart(2, '0');
  const month = (inputDate.getMonth() + 1).toString().padStart(2, '0');
  const year = inputDate.getFullYear();
  const hours = inputDate.getHours().toString().padStart(2, '0');
  const minutes = inputDate.getMinutes().toString().padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes} Uhr`;
}

